import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { Box, Divider, FormControl, IconButton, MenuItem, Select, SelectChangeEvent, Stack, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import moment from "moment";
import React, { Fragment, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { DashboardsContext } from "../../../contexts/DashboardsContext";
import { GroupSettingsContext } from '../../../contexts/GroupSettingsContext';
import { scrollbarStyle } from "../../../shared/dashboard";
import { Dashboard } from "../../../types/files";
import configuredAsyncComponentLoader, { FallbackLoading } from '../../templates/loader';

const DealsInsightGraph = configuredAsyncComponentLoader(
    () => import('./DealsInsightGraph'),
    { fallback: <FallbackLoading height="200px" />}
);

const TagGroupInsightGraph = configuredAsyncComponentLoader(
    () => import('./TagGroupInsightGraph'),
    { fallback: <FallbackLoading height="200px" />}
);

const TeamInsightGraph = configuredAsyncComponentLoader(
    () => import('./TeamInsightGraph'),
    { fallback: <FallbackLoading height="200px" />}
);

export enum TagTimeFrame {
    LAST_7_DAYS = 7,
    LAST_30_DAYS = 30,
    LAST_3_MONTHS = 90,   // ~3 months
    LAST_6_MONTHS = 180,  // ~6 months
    LAST_12_MONTHS = 365, // ~12 months / 1 year
};

export type InsightRange = {
    [key in TagTimeFrame]: {
        dashboards: Dashboard[];
    }
};

export type TagInsight = {
    tag: string;
    dayRanges: InsightRange;
};

const useStyles = makeStyles((theme) => ({
    block: {
        width: '100%',
        height: '100%',
        transition: '0.5s ease-in-out',
    },
    title: {
        fontFamily: 'Poppins',
        fontSize: '1.3rem !important',
        fontWeight: 600,
        color: theme.colors.neutral['700'],
    },
    manage: {
        fontFamily: 'Inter',
        fontSize: '1rem !important',
        fontWeight: 'bold',
        color: theme.palette.primary.main,
    },
    time: {
        fontFamily: 'Inter',
        fontSize: '0.85rem !important',
        fontWeight: 600,
        color: theme.colors.neutral['600'],
    },
    tabPanel: {
        height: '100%',
        width: '100%',
        padding: 'unset',
    },
    activity: {
        background: 'white',
        height: 'calc(100vh - 120px)',
        overflowY: 'hidden',
        overflowX: 'hidden',
    },
    feed: {
        width: '100%',
        height: '100%',
        overflowY: 'auto',
        overflowX: 'hidden',
        ...scrollbarStyle,
    },
    tableView: {
        width: '100%',
        minWidth: 'calc(100vw - 94px - 550px)',
        maxWidth: 'calc(100vw - 94px - 550px)',
        height: 'calc(100vh - 200px)',
        overflowY: 'auto',
        overflowX: 'hidden',
        ...scrollbarStyle,
    },
    subText: {
        fontFamily: 'Inter',
        fontSize: '1rem',
        fontWeight: 400,
        color: theme.colors.neutral['500'],
    },
    link: {
        fontFamily: 'Inter',
        fontSize: '1rem',
        fontWeight: 500,
        color: theme.palette.primary.light,
        cursor: 'pointer',
    },
    createButton: {
        width: '20ch',
        height: 40,
        borderRadius: 32,
        borderColor: theme.colors.neutral['100'],
        background: '#E0F1F1',
        color: theme.palette.primary.main,
        fontSize: '1rem',
        fontWeight: 'bold',
        textTransform: 'none',
        transition: 'ease-in-out 300ms',
    },
    manageButton: {
        width: '22ch',
        height: 40,
        borderRadius: 32,
        color: theme.palette.primary.main,
        fontSize: '1rem',
        fontWeight: 'bold',
        textTransform: 'none',
        transition: 'ease-in-out 300ms',
        "& > .MuiButton-startIcon": {
            width: 24,
            height: 24,
        },
    },
    manageIcon: {
        width: 20,
        height: 20,
        fill: theme.palette.primary.main,
        transform: 'rotate(270deg)',
    },
    formContainer: {
        height: 'auto',
        width: 'auto',
        paddingTop: '8px',
        paddingBottom: '8px',
        marginLeft: '8px',
        "& > .MuiInputBase-root": {
            justifyContent: 'center',
            fontFamily: 'Inter',
            color: '#666666',
            "& > div": {
                backgroundColor: 'unset !important',
            },
            "&::before": {
                content: 'unset !important',
            },
            "&::after": {
                content: 'unset !important',
            }
        }
    },
}));

const TimeFrameLabels: Record<TagTimeFrame, string> = {
    [TagTimeFrame.LAST_7_DAYS]: "Last 7 days",
    [TagTimeFrame.LAST_30_DAYS]: "Last 30 days",
    [TagTimeFrame.LAST_3_MONTHS]: "Last 3 months",
    [TagTimeFrame.LAST_6_MONTHS]: "Last 6 months",
    [TagTimeFrame.LAST_12_MONTHS]: "Last 12 months",
};

const PipelineInsights: React.FC<{ onExpand: (value: boolean) => void }> = ({ onExpand }) => {
    const classes = useStyles();
    const { dashboards, mappedOverviewQueries } = useContext(DashboardsContext);
    const { tagGroups, keyPeopleTags } = useContext(GroupSettingsContext);
    const [dealsInsights, setDealsInsights] = useState<InsightRange>({} as InsightRange);
    const [tagInsights, setTagInsights] = useState<{ tagGroup: string; tagInsights: TagInsight[]; }[]>([]);
    const [teamInsights, setTeamInsights] = useState<{ tagInsights: TagInsight[]; }>();
    const [timeFrame, setTimeFrame] = useState<TagTimeFrame>(TagTimeFrame.LAST_30_DAYS);
    const [display, setDisplay] = useState<'count'|'rate'>('count');
    const timerDealsRef = useRef<string | number | NodeJS.Timeout | undefined>(undefined);
    const timerTagGroupRef = useRef<string | number | NodeJS.Timeout | undefined>(undefined);
    const timerTeamRef = useRef<string | number | NodeJS.Timeout | undefined>(undefined);

    const sortedDashboards: Dashboard[] = useMemo(() =>
        [...dashboards].sort((dA, dB) => moment(dB.createdAt).diff(moment(dA.createdAt)))
    , [dashboards]);

    const handleTimeFrame = useCallback((event: SelectChangeEvent) => {
        setTimeFrame(event.target.value as unknown as TagTimeFrame);
    }, []);

    const handleDisplay = useCallback((event: SelectChangeEvent) => {
        setDisplay(event.target.value as unknown as 'count'|'rate');
    }, []);

    const handleDeals = useCallback(() => {
        const dayRanges = Object.values(TagTimeFrame)
            .filter((v) => typeof v === "number")
            .reduce((acc, timeframe) => {
                acc[timeframe as TagTimeFrame] = { dashboards: [] };
                return acc;
            }, {} as InsightRange);

        sortedDashboards.forEach(dashboard => {
            const diffInDays = moment().diff(dashboard.createdAt, "days");

            Object.values(TagTimeFrame)
                .filter((v) => typeof v === "number")
                .forEach((range) => {
                    if (diffInDays <= Number(range))
                        dayRanges[range as TagTimeFrame].dashboards.push(dashboard);
                });
        });

        return dayRanges;
    }, [sortedDashboards]);

    const handleDashboardTags = useCallback((tagGroup: string, tag: string) => {
        const dayRanges = Object.values(TagTimeFrame)
            .filter((v) => typeof v === "number")
            .reduce((acc, timeframe) => {
                acc[timeframe as TagTimeFrame] = { dashboards: [] };
                return acc;
            }, {} as InsightRange);

        sortedDashboards.forEach(dashboard => {
            const diffInDays = moment().diff(dashboard.createdAt, "days");
            const hasTag = dashboard.tagGroups
                ?.find(({title}) => title.toLowerCase() === tagGroup.toLowerCase())?.tags
                ?.find(t => t.toLowerCase() === tag.toLowerCase());

            if (hasTag) {
                Object.values(TagTimeFrame)
                    .filter((v) => typeof v === "number")
                    .forEach((range) => {
                        if (diffInDays <= Number(range))
                            dayRanges[range as TagTimeFrame].dashboards.push(dashboard);
                    });
            }
        });

        return dayRanges;
    }, [sortedDashboards]);

    const handleTeamTags = useCallback((tag: string) => {
        const dayRanges = Object.values(TagTimeFrame)
            .filter((v) => typeof v === "number")
            .reduce((acc, timeframe) => {
                acc[timeframe as TagTimeFrame] = { dashboards: [] };
                return acc;
            }, {} as InsightRange);

        sortedDashboards.forEach(dashboard => {
            const diffInDays = moment().diff(dashboard.createdAt, "days");
            const keyPeople = mappedOverviewQueries.get(`${dashboard!.id}:Key People`);

            if (Array.isArray(keyPeople) && !!keyPeople.length) {
                const hasTag = keyPeople.some(({tags}) => tags?.map((t: string) => t.toUpperCase())?.includes(tag.toUpperCase()));

                if (hasTag) {
                    Object.values(TagTimeFrame)
                        .filter((v) => typeof v === "number")
                        .forEach((range) => {
                            if (diffInDays <= Number(range))
                                dayRanges[range as TagTimeFrame].dashboards.push(dashboard);
                        });
                }
            }
        });

        return dayRanges;
    }, [sortedDashboards, mappedOverviewQueries]);

    useEffect(() => {
        timerDealsRef.current = setTimeout(() => {
            clearTimeout(timerDealsRef.current);
            setDealsInsights(handleDeals());
        }, 300);

        return () => {
            clearTimeout(timerDealsRef.current);
        }
    }, [handleDeals]);

    useEffect(() => {
        timerTagGroupRef.current = setTimeout(() => {
            clearTimeout(timerTagGroupRef.current);
            setTagInsights(tagGroups.map(({title, tags}) => ({
                tagGroup: title,
                tagInsights: tags.map(tag => ({ tag, dayRanges: handleDashboardTags(title, tag) })),
            })));
        }, 300);

        return () => {
            clearTimeout(timerTagGroupRef.current);
        }
    }, [tagGroups, handleDashboardTags]);

    useEffect(() => {
        timerTeamRef.current = setTimeout(() => {
            clearTimeout(timerTeamRef.current);
            setTeamInsights({ tagInsights: keyPeopleTags.map(tag => ({ tag, dayRanges: handleTeamTags(tag) })) });
        }, 300);

        return () => {
            clearTimeout(timerTeamRef.current);
        }
    }, [keyPeopleTags, handleTeamTags]);

    return (<>
        <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" pt={1}>
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-start" ml={2} >
                <TrendingUpIcon sx={{ fill: 'rgba(0, 0, 0, 0.5)' }} />
                <Typography className={classes.title} ml={1}>
                    {'Pipeline Insights'}
                </Typography>
            </Stack>
            <Box mr="16px !important">
                <IconButton onClick={(e) => {
                    e.stopPropagation();
                    onExpand(false);
                }}> <ArrowForwardIosIcon /> </IconButton>
            </Box>
        </Stack>
        <Stack direction="row" spacing={2} alignItems="center" justifyContent="flex-end" width="100%">
            <FormControl className={classes.formContainer} variant={'standard'}>
                <Select value={display} onChange={handleDisplay}
                    renderValue={(selected) => (
                        <Typography component="span"
                            className={classes.time}
                            color={`#aeaeae !important`}
                            display="inline">
                            {selected === 'count' ? 'Count' : 'Percentage'}
                        </Typography>
                    )} sx={{
                        }}
                    displayEmpty>
                    <MenuItem value={'count'}
                        sx={{ fontSize: '0.85rem', color: (display === 'count') ? '#aeaeae' : 'gray', }}>
                        {'Count'}
                    </MenuItem>
                    <MenuItem value={'rate'}
                        sx={{ fontSize: '0.85rem', color: (display === 'rate') ? '#aeaeae' : 'gray', }}>
                        {'Percentage'}
                    </MenuItem>
                </Select>
            </FormControl>
            <FormControl className={classes.formContainer} variant={'standard'}>
                <Select value={String(timeFrame)} onChange={handleTimeFrame}
                    renderValue={(selected) => (
                        <Typography component="span"
                            className={classes.time}
                            color={`#aeaeae !important`}
                            display="inline">
                            {TimeFrameLabels[selected as unknown as TagTimeFrame]}
                        </Typography>
                    )}
                    displayEmpty>
                    <MenuItem value={TagTimeFrame.LAST_7_DAYS}
                        sx={{ fontSize: '0.85rem', color: (timeFrame === TagTimeFrame.LAST_7_DAYS) ? '#aeaeae' : 'gray', }}
                        style={{ display: 'none' }}>
                        {TimeFrameLabels[TagTimeFrame.LAST_7_DAYS]}
                    </MenuItem>
                    <MenuItem value={TagTimeFrame.LAST_30_DAYS}
                        sx={{ fontSize: '0.85rem', color: (timeFrame === TagTimeFrame.LAST_30_DAYS) ? '#aeaeae' : 'gray', }}>
                        {TimeFrameLabels[TagTimeFrame.LAST_30_DAYS]}
                    </MenuItem>
                    <MenuItem value={TagTimeFrame.LAST_3_MONTHS}
                        sx={{ fontSize: '0.85rem', color: (timeFrame === TagTimeFrame.LAST_3_MONTHS) ? '#aeaeae' : 'gray', }}>
                        {TimeFrameLabels[TagTimeFrame.LAST_3_MONTHS]}
                    </MenuItem>
                    <MenuItem value={TagTimeFrame.LAST_6_MONTHS}
                        sx={{ fontSize: '0.85rem', color: (timeFrame === TagTimeFrame.LAST_6_MONTHS) ? '#aeaeae' : 'gray', }}>
                        {TimeFrameLabels[TagTimeFrame.LAST_6_MONTHS]}
                    </MenuItem>
                    <MenuItem value={TagTimeFrame.LAST_12_MONTHS}
                        sx={{ fontSize: '0.85rem', color: (timeFrame === TagTimeFrame.LAST_12_MONTHS) ? '#aeaeae' : 'gray', }}>
                        {TimeFrameLabels[TagTimeFrame.LAST_12_MONTHS]}
                    </MenuItem>
                </Select>
            </FormControl>
        </Stack>
        <Divider sx={{ width: '100%' }} />
        <Stack direction="column" className={classes.feed} spacing={2} alignItems="stretch" p={2}>
            <DealsInsightGraph dealsInsights={dealsInsights} timeFrame={timeFrame} />
            <Divider sx={{ width: '100%' }} />
            {tagInsights.map(({tagGroup, tagInsights}, i) => (
                <Fragment key={`pipeline-insight-175-${i}`}>
                    <TagGroupInsightGraph
                        tagGroup={tagGroup}
                        tagInsights={tagInsights}
                        timeFrame={timeFrame}
                        display={display} />
                    <Divider sx={{ width: '100%' }} />
                </Fragment>
            ))}
            <TeamInsightGraph
                teamInsights={teamInsights ? teamInsights.tagInsights : []}
                timeFrame={timeFrame}
                display={display} />
        </Stack>
    </>);
}

export default PipelineInsights;
